import { api } from "./api";
import type { CurrentUser } from "@/context/AuthContext";
import { isAxiosError } from "axios";
import type { OrderDifference } from "./procurementDifferences";
import type { ServiceDetails, ServiceExecution, ServiceInput, ServiceUnit } from "./serviceProcurement";

export type OrderState = "skica" | "ceka_odobrenje" | "vraceno" | "odobreno" | "poslano" | "djelomicno_stiglo" | "stiglo" | "otkazano";
export const orderStates: Record<OrderState, string> = {
  skica: "Skica", ceka_odobrenje: "Čeka odobrenje", vraceno: "Vraćeno", odobreno: "Odobreno",
  poslano: "Poslano", djelomicno_stiglo: "Djelomično stiglo", stiglo: "Stiglo", otkazano: "Otkazano",
};
export const units = ["kom", "par", "m", "m2", "kg", "l", "pak", "m3", "t"] as const;
export interface Supplier {
  id: string; naziv: string; oib: string | null; email_narudzbi: string | null;
  kontakt_osoba: string | null; telefon: string | null; napomena: string | null; aktivan: boolean;
}
export type SupplierInput = Omit<Supplier, "id">;
export interface SupplierContact {
  id: string; dobavljac_id: string; ime_prezime: string; funkcija: string | null;
  email: string | null; telefon: string | null; aktivan: boolean; glavni: boolean;
  redoslijed: number; created_at: string; updated_at: string;
}
export type SupplierContactInput = Pick<SupplierContact, "ime_prezime" | "funkcija" | "email" | "telefon" | "glavni">;
export interface RecipientInput {
  primatelj_kontakt_id?: string | null; primatelj_ime?: string | null;
  primatelj_email?: string | null; primatelj_telefon?: string | null; spremi_u_kontakte?: boolean;
}
export interface RecipientSnapshot {
  primatelj_kontakt_id?: string | null; primatelj_ime?: string | null;
  primatelj_email?: string | null; primatelj_telefon?: string | null; primatelj_funkcija?: string | null;
  primatelj_spremanje_kontakta?: "spremljeno" | "preskoceno_bez_ovlasti" | "nije_zatrazeno";
}
export function recipientLabel(order: RecipientSnapshot) {
  return [order.primatelj_ime, order.primatelj_email, order.primatelj_telefon].filter(Boolean).join(" · ") || "Nije upisan";
}
export function recipientSaveNotice(order: RecipientSnapshot) {
  return order.primatelj_spremanje_kontakta === "spremljeno" ? " Kontakt je spremljen kod dobavljača." : order.primatelj_spremanje_kontakta === "preskoceno_bez_ovlasti" ? " Primatelj je spremljen na narudžbi. Kontakt nije dodan dobavljaču jer nemate ovlast." : "";
}
export interface SupplierSummary extends Supplier {
  otvorene_narudzbe: number; zadnja_narudzba: string | null; ukupno_godina: string | null; godina: number;
}
export interface SupplierDetail extends SupplierSummary { ukupno_po_godini: Record<string, string> | null }
export interface OrderItemInput {
  materijal_id?: string | null; razred_betona?: string | null; termin_isporuke?: string | null;
  pumpa?: boolean | null; mjesto_ugradnje?: string | null; napomena_betonari?: string | null;
  naziv: string; kolicina: string; jedinica: typeof units[number] | ServiceUnit; cijena: string | null;
  gradiliste_id?: string | null; vrsta_ozo_id?: string | null; velicina?: string | null; napomena?: string | null; napomena_dobavljacu?: boolean;
}
export interface OrderItem extends OrderItemInput { id: string; primljeno: string; gradiliste?: string | null; izvorna_stavka_id?: string | null }
export interface OrderInput extends RecipientInput {
  kategorija?: "sitno" | "gradilisni" | "usluga";
  usluga?: ServiceInput | null;
  gradiliste_id: string; dobavljac_id: string; vrsta: "obicna" | "telefonom" | "skupna";
  svrha: string; napomene?: string | null; napomena_dobavljacu?: boolean; okvirni_iznos: string | null; rok_isporuke: string | null;
  mjesto_isporuke: string | null; poslano_biljeska?: string | null; stavke: OrderItemInput[];
}
export interface OrderSummary {
  razlike_kasne?: boolean;
  kategorija?: "sitno" | "gradilisni" | "usluga";
  usluga?: ServiceDetails | null;
  id: string; broj: string | null; created_at: string; updated_at: string; datum?: string | null; gradiliste_id: string;
  gradiliste: string; dobavljac_id: string; dobavljac: string; dobavljac_podaci?: Supplier; narucitelj_id: string;
  narucitelj: string; svrha: string; stanje: OrderState; okvirni_iznos: string | null;
  ukupno_stavke: string | null; ceka_mene: boolean; vrsta: OrderInput["vrsta"];
  skupna_narudzba_id: string | null; skupna_broj?: string | null; trazi_naknadno_odobrenje: boolean;
  mail_dostupan: boolean; mail_nedostupan_razlog: string | null;
  mail_stanje: "slanje" | "poslano" | "greska" | null;
  mail_greska: string | null; mail_primatelj: string | null; vazno: boolean;
}
export interface ProcurementDocument { id: string; naziv: string; mime_type: string; content_url: string; tip_dokumenta?: string }
export interface Receipt {
  odrediste?: "gradiliste" | "ured";
  broj_otpremnice?: string | null; stavke?: { stavka_id: string; kolicina: string }[];
  id: string; datum: string; vrsta: "sve" | "djelomicno" | "nista"; napomena: string | null;
  dokument_id: string | null; bez_otpremnice_razlog?: string | null;
}
export interface Order extends OrderSummary, RecipientSnapshot {
  izvrsenja?: ServiceExecution[];
  tolerancija_posto?: string; zapisnik?: { razlog: string; tekst: string; inzenjer: string; poslovodja: string; vozac: string } | null;
  razlike?: OrderDifference[];
  poslao_id: string | null; poslao: string | null; poslao_email: string | null; poslao_telefon: string | null;
  slanje_bez_telefona: boolean; napomene: string | null; napomena_dobavljacu: boolean;
  stavke: OrderItem[]; rok_isporuke: string | null; mjesto_isporuke: string | null;
  vraceno_poruka: string | null; poslano_biljeska: string | null; pdf_dokument_id: string | null;
  primici: Receipt[]; dokumenti: ProcurementDocument[];
  dnevnik: { id: string; vrsta: string; tekst: string | null; korisnik_id: string; korisnik: string; vazno: boolean; created_at: string }[];
  sljedeci_korak: { id: "odobreno" | "poslano" | "otpremnica" | "sve_stiglo" | "dani_potvrdeni" | "danas_upisano" | "usluga_zavrsena"; naziv: string; ispunjeno: boolean; iznimka?: boolean; zavrseno_at: string | null; izvrsitelj_id: string | null; izvrsitelj: string | null }[];
}
export interface ProcurementSettings {
  nabava_prag_odobrenja: string; nabava_pocetni_broj: Record<string, number>;
  nabava_email_posiljatelj: string | null;
  nabava_email_kopija: string | null;
  mail_configured: boolean;
}
export type OrderCounts = Record<OrderState, { broj: number; na_meni: number }> & { razlike?: { broj: number; na_meni: number; kasni: number } };
export interface OrderFilters { gradiliste?: string; dobavljac?: string; stanje?: string; mjesec?: string; q?: string; razlike?: string; kategorija?: string }
const root = "/nabava";
async function content(url: string, signal?: AbortSignal) {
  try { return (await api.get<Blob>(url, { responseType: "blob", signal })).data; }
  catch (cause) {
    if (isAxiosError(cause) && cause.response?.data instanceof Blob) {
      try { cause.response.data = JSON.parse(await cause.response.data.text()) as unknown; } catch { /* Keep the readable fallback for non-JSON failures. */ }
    }
    throw cause;
  }
}
export const procurement = {
  contacts: async (supplier: string, signal?: AbortSignal) => (await api.get<SupplierContact[]>(`${root}/dobavljaci/${supplier}/kontakti`, { signal })).data,
  saveContact: async (supplier: string, input: SupplierContactInput, id?: string) => (await (id ? api.patch<SupplierContact>(`${root}/dobavljaci/${supplier}/kontakti/${id}`, input) : api.post<SupplierContact>(`${root}/dobavljaci/${supplier}/kontakti`, input))).data,
  deactivateContact: async (supplier: string, id: string) => (await api.delete<SupplierContact>(`${root}/dobavljaci/${supplier}/kontakti/${id}`)).data,
  counts: async (params: Omit<OrderFilters, "stanje"> = {}, signal?: AbortSignal) => (await api.get<OrderCounts>(`${root}/narudzbe/brojevi`, { params, signal })).data,
  deleteSupplier: async (id: string) => api.delete(`${root}/dobavljaci/${id}`),
  deleteOrder: async (id: string) => api.delete(`${root}/narudzbe/${id}`),
  suppliers: async (signal?: AbortSignal) => (await api.get<SupplierSummary[]>(`${root}/dobavljaci`, { signal })).data,
  supplier: async (id: string, signal?: AbortSignal) => (await api.get<SupplierDetail>(`${root}/dobavljaci/${id}`, { signal })).data,
  saveSupplier: async (input: SupplierInput, id?: string) =>
    (await (id ? api.patch<Supplier>(`${root}/dobavljaci/${id}`, input) : api.post<Supplier>(`${root}/dobavljaci`, input))).data,
  orders: async (params: OrderFilters = {}, signal?: AbortSignal) => (await api.get<OrderSummary[]>(`${root}/narudzbe`, { params, signal })).data,
  approvals: async (signal?: AbortSignal) => (await api.get<OrderSummary[]>(`${root}/narudzbe/ceka-odobrenje`, { signal })).data,
  order: async (id: string, signal?: AbortSignal) => (await api.get<Order>(`${root}/narudzbe/${id}`, { signal })).data,
  save: async (input: OrderInput, id?: string) => {
    // Immutable creation fields are intentionally absent from the PATCH schema.
    const patch = { gradiliste_id: input.gradiliste_id, dobavljac_id: input.dobavljac_id, svrha: input.svrha, napomene: input.napomene, napomena_dobavljacu: input.napomena_dobavljacu, okvirni_iznos: input.okvirni_iznos, rok_isporuke: input.rok_isporuke, mjesto_isporuke: input.mjesto_isporuke, stavke: input.stavke };
    const recipientFields = ["primatelj_kontakt_id", "primatelj_ime", "primatelj_email", "primatelj_telefon", "spremi_u_kontakte"] as const;
    const recipient = Object.fromEntries(recipientFields.filter(key => input[key] !== undefined).map(key => [key, input[key]]));
    return (await (id ? api.patch<Order>(`${root}/narudzbe/${id}`, { ...patch, ...recipient, ...(input.usluga ? { usluga: input.usluga } : {}) }) : api.post<Order>(`${root}/narudzbe`, input))).data;
  },
  action: async (id: string, action: "posalji-na-odobrenje" | "odobri" | "vrati" | "posalji" | "posalji-ponovno" | "otkazi", input: object = {}) =>
    (await api.post<Order>(`${root}/narudzbe/${id}/${action}`, action === "posalji-ponovno" ? undefined : input)).data,
  addNote: async (id: string, tekst: string, vazno: boolean) => (await api.post<Order>(`${root}/narudzbe/${id}/biljeska`, { tekst, vazno })).data,
  combine: async (dobavljac_id: string, narudzba_ids: string[], gradiliste_id?: string) => (await api.post<Order>(`${root}/narudzbe/skupna`, { dobavljac_id, narudzba_ids, ...(gradiliste_id ? { gradiliste_id } : {}) })).data,
  suggestions: async (dobavljac: string, q: string, signal?: AbortSignal) =>
    (await api.get<Pick<OrderItemInput, "naziv" | "jedinica">[]>(`${root}/narudzbe/prijedlog-stavki`, { params: { dobavljac, q }, signal })).data,
  shortages: async (site: string, signal?: AbortSignal) => (await api.get<OrderItemInput[]>(`/gradilista/${site}/oprema/naruci-sto-fali`, { signal })).data,
  settings: async (signal?: AbortSignal) => (await api.get<Partial<ProcurementSettings>>("/postavke", { signal })).data,
  saveSettings: async (input: Pick<ProcurementSettings, "nabava_prag_odobrenja" | "nabava_pocetni_broj" | "nabava_email_kopija"> & { razlog: string }) =>
    (await api.patch<ProcurementSettings>("/postavke", input)).data,
  receive: async (id: string, input: FormData) => (await api.post<Order>(`${root}/narudzbe/${id}/primitak`, input)).data,
  pdf: async (id: string, signal?: AbortSignal) => content(`${root}/narudzbe/${id}/pdf`, signal),
  document: async (url: string) => {
    if (!url.startsWith("/api/") || url.startsWith("/api//")) throw new Error("Document URL must be relative to the API");
    return content(url.slice(4));
  },
};
export function hasProcurement(user: CurrentUser | null, action?: string, scope?: string) {
  return user?.permissions?.some(p => p.module === "nabava" && (action ? p.action === action : ["read", "create", "approve", "receive"].includes(p.action)) && (!scope || p.scope === scope)) ?? false;
}
export function canEditOrder(user: CurrentUser | null, order: OrderSummary) {
  return ["skica", "vraceno"].includes(order.stanje) &&
    (hasProcurement(user, "update", "all") || (order.narucitelj_id === user?.id && hasProcurement(user, "update")));
}
/** Decimal strings remain exact, including the approval boundary. */
export function decimalUnits(value: string, digits = 2): bigint | null {
  const normal = value.trim().replace(",", ".");
  if (!new RegExp(`^\\d{1,${12 - digits}}(?:\\.\\d{1,${digits}})?$`).test(normal)) return null;
  const [whole, part = ""] = normal.split(".");
  return BigInt(whole!) * 10n ** BigInt(digits) + BigInt(part.padEnd(digits, "0"));
}
export function decimalString(value: string, digits = 2): string | null {
  const amount = decimalUnits(value, digits);
  if (amount === null) return null;
  const raw = amount.toString().padStart(digits + 1, "0");
  return `${raw.slice(0, -digits)}.${raw.slice(-digits)}`;
}
export function money(value: string | null | undefined) {
  if (value == null) return "Nije upisano";
  const amount = decimalUnits(String(value));
  if (amount === null) return "Nije upisano";
  const raw = amount.toString().padStart(3, "0");
  return `${raw.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${raw.slice(-2)} €`;
}
export function orderLabel(order: OrderSummary) { return order.broj || order.skupna_broj || "Bez dodijeljenog broja"; }
/** Format decimal API strings without rounding or converting to binary numbers. */
export function quantity(value: string | null | undefined) {
  if (value == null || !/^\d+(?:[.,]\d+)?$/.test(value)) return "—";
  const [whole, part = ""] = value.replace(",", ".").split(".");
  const fraction = part.replace(/0+$/, "");
  return `${whole!.replace(/^0+(?=\d)/, "")}${fraction ? `,${fraction}` : ""}`;
}
export function primaryLabel(amount: string, threshold?: string, telephone = false) {
  if (telephone) return "Zabilježi narudžbu";
  const value = decimalUnits(amount), limit = threshold == null ? null : decimalUnits(String(threshold));
  return value !== null && limit !== null && value <= limit ? "Naruči" : "Pošalji na odobrenje";
}
export function remaining(item: OrderItem) {
  const wanted = decimalUnits(item.kolicina, 3), got = decimalUnits(item.primljeno, 3);
  if (wanted === null || got === null) return "";
  const raw = (wanted > got ? wanted - got : 0n).toString().padStart(4, "0");
  return `${raw.slice(0, -3)}.${raw.slice(-3)}`;
}
